import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { FaBars, FaTimes, FaFolderOpen, FaUserFriends } from 'react-icons/fa';
import Referrals from './Referrals';
import Projects from './Projects';
import { API_BASE, Functionality } from '../context/Functionality';

export default function AccountPortal() {
  const { token, user, logout } = useContext(Functionality);
  const { id } = useParams();
  const location = useLocation();
  const [params] = useSearchParams();
  const section = params.get('section') === 'referrals' ? 'referrals' : 'projects';
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebar = useRef(null);
  const toggle = useRef(null);
  const portalId = id || 'me';
  useEffect(() => {
    let active = true;
    setResult(null); setError('');
    axios.get(`${API_BASE}/api/portals/${portalId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { if (active) setResult({ ...data, portalId }); })
      .catch(err => { if (active) setError(err.response?.data?.errMsg || 'Unable to load the portal. Please try again.'); });
    return () => { active = false; };
  }, [token, portalId]);
  useEffect(() => { setSidebarOpen(false); }, [section, portalId]);
  useEffect(() => {
    if (!sidebarOpen) return;
    function dismiss(event) {
      if (event.type === 'keydown') {
        if (event.key === 'Escape') { setSidebarOpen(false); toggle.current?.focus(); }
      } else if (!sidebar.current?.contains(event.target) && !toggle.current?.contains(event.target)) setSidebarOpen(false);
    }
    document.addEventListener('mousedown', dismiss);
    document.addEventListener('keydown', dismiss);
    return () => { document.removeEventListener('mousedown', dismiss); document.removeEventListener('keydown', dismiss); };
  }, [sidebarOpen]);
  const account = result?.portalId === portalId ? result : null;
  function sectionUrl(value) {
    const next = new URLSearchParams(params);
    next.set('section', value);
    return `${location.pathname}?${next}`;
  }
  return <div className="flex min-h-screen flex-col md:flex-row bg-gray-50">
    <aside id="client-sidebar" ref={sidebar} className={`${sidebarOpen ? 'flex' : 'hidden'} fixed bottom-0 top-16 left-0 z-30 w-60 shrink-0 flex-col overflow-y-auto bg-[#0e2234] text-white md:relative md:top-0 md:flex`}>
      <div className="relative p-4">
        <Link to="/" aria-label="Blake Technologies home"><img width="100" height="100" className="mx-auto" src="/Logowhite.png" alt="Blake Technologies" /></Link>
        <button type="button" className="absolute right-3 top-3 rounded p-2 md:hidden" aria-label="Close client sidebar" onClick={() => { setSidebarOpen(false); toggle.current?.focus(); }}><FaTimes aria-hidden="true" /></button>
      </div>
      <div className="border-y border-white/10 px-4 py-5">
        <p className="text-xs uppercase tracking-wider text-gray-400">Client portal</p>
        {account && <><p className="mt-2 font-semibold break-words">{account.owner.firstName} {account.owner.lastName}</p><p className="mt-1 text-sm text-gray-300 break-words">{account.owner.email}</p></>}
      </div>
      <nav aria-label="Client portal" className="space-y-2 px-3 py-4">
        {[['projects', 'Projects', FaFolderOpen], ['referrals', 'Referrals', FaUserFriends]].map(([value, label, Icon]) => <Link key={value} to={sectionUrl(value)} aria-current={section === value ? 'page' : undefined} onClick={() => setSidebarOpen(false)} className={`flex items-center gap-3 rounded-lg p-3 font-medium ${section === value ? 'bg-indigo-700 text-white' : 'text-gray-200 hover:bg-gray-700'}`}><Icon aria-hidden="true" size={20} />{label}</Link>)}
      </nav>
      <div className="mt-auto border-t border-white/10 p-4 space-y-3">
        {user.isEmployee && <Link className="block text-sm text-gray-200 underline" to="/account-portals">Back to employee portal</Link>}
        <button type="button" onClick={logout} className="w-full rounded-lg border border-gray-500 px-3 py-2 text-left hover:bg-gray-700">Sign out</button>
      </div>
    </aside>
    <main className="min-w-0 flex-1 p-4 md:p-8">
      <div className="mb-6 flex items-center gap-3">
        <button ref={toggle} type="button" onClick={() => setSidebarOpen(value => !value)} aria-label="Toggle client sidebar" aria-expanded={sidebarOpen} aria-controls="client-sidebar" className="rounded-lg border bg-white p-3 md:hidden"><FaBars aria-hidden="true" /></button>
        <h1 className="text-xl font-semibold text-gray-700">{id && id !== user._id ? 'Account portal' : 'Your portal'}</h1>
      </div>
      {error && <p role="alert">{error}</p>}
      {!error && !account && <p role="status">Loading portal…</p>}
      {account && <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6">
        {section === 'projects' ? <Projects key={`projects-${account.id}`} ownerId={account.id} hideWhenEmpty={false} /> : <Referrals key={`referrals-${account.id}`} ownerId={account.id} />}
      </div>}
    </main>
  </div>;
}
